<template>
  <BaseModalEditCreate
    :title="isNew ? $t('common.actions.create') : modifiedData?.materialAlterCode.alterCode.name"
    v-if="isOpen"
    @dismissChanges="onDismissChanges"
    @save="onSave"
    maxWidth="extra"
    :isModified="shouldWarnBeforeClose"
    :hideSaveButton="mode == 'view' ? true : false">
    <template #form>
      <div v-if="competitorAlterCode && modifiedData" class="w-full h-full overflow-y-auto">
        <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
          <tbody>
            <BaseDetailsTableRow :name="$t('material.material')" :mode="mode">
              <BaseInput
                v-if="modifiedData.material"
                v-model="modifiedData.material.name"
                color="base"
                mode="view"
                flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.competitor')" :mode="mode">
              <BaseInput v-model="modifiedData.competitor.code" color="base" mode="view" flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.alterCodeType')" :mode="mode">
              <BaseSelectDropdown
                v-if="dropDownAlterCodeTypes && dropDownAlterCodeTypes.length > 0"
                :items="dropDownAlterCodeTypes"
                :selected-item="selectedAlterCodetype"
                name="alterCodeType"
                flat
                size="sm"
                mode="view" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.name')" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.alterCode.name"
                color="base"
                :mode="mode"
                flat
                show-error-as-tooltip
                :error="$v.name.required.$invalid ? $v.name.required.$message : ''"
                class="w-full" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.code')" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.alterCode.code"
                color="base"
                :mode="mode"
                show-error-as-tooltip
                :error="$v.code.required.$invalid ? $v.code.required.$message : ''"
                flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.smallCode')" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.shortCode"
                color="base"
                :mode="mode"
                flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.measurementUnit')" :mode="mode">
              <BaseSelectDropdown
                v-if="dropdownMeasurementUnits && dropdownMeasurementUnits.length > 0"
                :items="dropdownMeasurementUnits"
                :selected-item="selectedMeasurementUnit"
                name="measurementUnit"
                flat
                :mode="mode"
                clearable
                show-error-as-tooltip
                :error="
                  $v.measurementUnitId.required.$invalid
                    ? $v.measurementUnitId.required.$message
                    : ''
                "
                @update:selected="handleMeasurementSelection" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.relationFactor')" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.relationFactor"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.minSalesQuantity')" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.minSalesQuantity"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.lotStart')" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.lotStart"
                color="base"
                :mode="mode"
                flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.lotFinish')" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.lotFinsh"
                color="base"
                :mode="mode"
                flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.dimension', { 'number': 1 })" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.dimension1"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.dimension', { 'number': 2 })" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.dimension2"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.dimension', { 'number': 3 })" :mode="mode">
              <BaseInput
                v-model="modifiedData.materialAlterCode.dimension3"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.quantityTotalizer')" :mode="mode">
              <BaseTableCellStatus
                v-if="mode == 'view'"
                :item="
                  modifiedData.materialAlterCode.quantityTotalizer
                    ? modifiedData.materialAlterCode.quantityTotalizer
                    : 0
                " />
              <BaseToggle
                v-else
                :label="$t('material.quantityTotalizer')"
                name="quantityTotalizer"
                v-model="modifiedData.materialAlterCode.quantityTotalizer" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.default')" :mode="mode">
              <BaseTableCellStatus
                v-if="mode == 'view'"
                :item="modifiedData.isDefault ? modifiedData.isDefault : 0" />
              <BaseToggle v-else label="Default" name="default" v-model="modifiedData.isDefault" />
            </BaseDetailsTableRow>
          </tbody>
        </table>
      </div> </template
  ></BaseModalEditCreate>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { deepCopy } from '@/utils/data';
import { helpers, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import BaseDetailsTableRow from '@/components/common/BaseDetailsTableRow.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseTableCellStatus from '@/components/common/BaseTableCellStatus.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import type { SelectDropdownItem } from '@/types/Base';
import type { CompetitorMaterial } from '@/types/Materials';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  competitorAlterCode: CompetitorMaterial;
  isOpen: boolean;
  isNew: boolean;
}>();

const emit = defineEmits(['close', 'save']);

const modifiedData = ref<CompetitorMaterial>(
  deepCopy<CompetitorMaterial>(props.competitorAlterCode)
);

const selectedMeasurementUnit = ref<SelectDropdownItem | null>(null);
const selectedAlterCodetype = ref<SelectDropdownItem | null>(null);
const mode = ref<'view' | 'edit'>('view');

const { dropdownMeasurementUnits, dropDownAlterCodeTypes } = storeToRefs(useLookupsStore());

function initDropdowns() {
  if (modifiedData.value) {
    const measurementValue = dropdownMeasurementUnits.value.find(
      (item) => item.value == modifiedData.value?.materialAlterCode.measurementUnitId
    );
    if (measurementValue) selectedMeasurementUnit.value = measurementValue;
    else selectedMeasurementUnit.value = null;

    const alterCodeTypeValue = dropDownAlterCodeTypes.value.find(
      (item) => item.value == modifiedData.value?.materialAlterCode.alterCode.alterCodeTypeId
    );
    if (alterCodeTypeValue) selectedAlterCodetype.value = alterCodeTypeValue;
    else selectedAlterCodetype.value = null;
  }
}

const validationModel = ref({
  name: modifiedData.value.materialAlterCode.alterCode.name || '',
  code: modifiedData.value.materialAlterCode.alterCode.code || '',
  measurementUnitId: modifiedData.value.materialAlterCode.measurementUnitId || null,
});

const { t: $t } = useI18n({ useScope: 'global' });

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  measurementUnitId: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function handleMeasurementSelection(item: SelectDropdownItem) {
  selectedMeasurementUnit.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.materialAlterCode.measurementUnitId = item
      ? (item.value as string)
      : undefined;
    modifiedData.value.materialAlterCode.measurementUnit.name = item ? (item.name as string) : '';
    validationModel.value.measurementUnitId = item ? (item.value as string) : null;
    $v.value.measurementUnitId.$touch();
  }
}

const modalCloseWarningActive = ref(false);
const shouldWarnBeforeClose = ref<boolean>(false);

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  emit('save', modifiedData.value);
  shouldWarnBeforeClose.value = false;
  mode.value = 'view';
  $v.value.$reset();
}

function onDismissChanges() {
  emit('close');
  modifiedData.value = deepCopy<CompetitorMaterial>(props.competitorAlterCode);
  modalCloseWarningActive.value = false;
  shouldWarnBeforeClose.value = false;
  mode.value = 'view';
  $v.value.$reset();
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.materialAlterCode.alterCode.name || '';
    validationModel.value.code = newData.materialAlterCode.alterCode.code || '';
    validationModel.value.measurementUnitId = newData.materialAlterCode.measurementUnitId || null;

    const isSame = JSON.stringify(modifiedData.value) === JSON.stringify(props.competitorAlterCode);

    shouldWarnBeforeClose.value = !isSame;
  },
  { deep: true }
);

watch(
  () => props.competitorAlterCode,
  () => {
    modifiedData.value = deepCopy<CompetitorMaterial>(props.competitorAlterCode);
    initDropdowns();
  }
);

watch(
  () => props.competitorAlterCode,
  (newValue) => {
    if (newValue) mode.value = newValue.isFromManager ? 'edit' : 'view';
  },
  { immediate: true }
);

onMounted(async () => {
  initDropdowns();
});
</script>
